// Check-in / venue picker — canon port of CheckInScreen
// (apps/web/src/design-canon/project/src/screens-flow.jsx :: CheckInScreen).
//
// Wires apps/api: GET /api/venues/search + POST /api/venues + POST /api/nights/:id/venues.
//
// Per docs/UI_SPEC.md §3:
//   scroll: ALLOWED (venue list)
//   primary: "Tap in" (after selection)
//   secondary: live search + "don't see your bar?" → AddVenueSheet
//
// Per ADR-0018: StumbleMap is the stylized treasure variant — no tile-fetch.
//
// House / private-spot persistence: the API venues table has no `kind` field
// today (provider check is 'local' | 'google_places' only). To preserve the
// canon's "private spots" section without a schema migration, we mark
// custom-added houses client-side via localStorage (`stumble.private-venues`,
// a Record<venueId, { kind, owner }>). Houses are never pinned on the map and
// surface under their own header. Schema gap flagged in the commit message.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { type VenueDto, nightApi, venuesApi } from '../../../api'
import { Icon } from '../../../components/Icon'
import type { PinSpec } from '../../../components/Pin'
import { StumbleMap } from '../../../components/StumbleMap'
import { BackButton, TopBar } from '../../../components/TopBar'
import { type Coord, computeBounds, coordToPercent } from '../../../lib/geo'
import { useVisualViewport } from '../../../lib/useVisualViewport'

export const Route = createFileRoute('/night/$id/venue')({
  component: VenueRoute,
})

// ───── Color hash (matches your-venues.tsx) ─────────────────────
const VENUE_COLORS = [
  'var(--pink)',
  'var(--orange)',
  'var(--teal)',
  'var(--lime)',
  'var(--plum)',
  'var(--acid)',
] as const

function venueColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0
  return VENUE_COLORS[h % VENUE_COLORS.length] ?? VENUE_COLORS[0]
}

// ───── Private-spots local persistence (API gap workaround) ─────
// Why: the venues schema has no `kind` field. Storing house markers
// client-side keeps the canon UX intact until a follow-up migration
// adds a real column. Friendly format → easy to migrate later.
type PrivateKind = 'house' | 'other'
type PrivateMeta = { kind: PrivateKind; owner?: string }
const PRIVATE_KEY = 'stumble.private-venues'

function readPrivateMap(): Record<string, PrivateMeta> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(PRIVATE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, PrivateMeta>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writePrivateMap(map: Record<string, PrivateMeta>): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(PRIVATE_KEY, JSON.stringify(map))
  } catch {
    // quota / private-browsing — non-fatal; venue still saved server-side.
  }
}

type CustomKind = 'bar' | 'house' | 'other'

function VenueRoute() {
  const navigate = useNavigate()
  const { id: nightId } = Route.useParams()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<VenueDto[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [privateMap, setPrivateMap] = useState<Record<string, PrivateMeta>>(() => readPrivateMap())
  // F7: keyboard-open detection. When the soft keyboard pushes the visual
  // viewport up, --vv-bottom-chrome > 0 — we collapse the map to a thin pill
  // so the search input + results stay visible.
  const vv = useVisualViewport()
  const keyboardOpen = vv.bottomChrome > 80

  // Custom-add sheet state.
  const [customOpen, setCustomOpen] = useState(false)
  const [customName, setCustomName] = useState('')
  const [customKind, setCustomKind] = useState<CustomKind>('bar')
  const [customCity, setCustomCity] = useState('')
  const customNameRef = useRef<HTMLInputElement | null>(null)

  // Focus the custom-name input after a frame so screen-readers announce the
  // sheet first (a11y/noAutofocus per Biome rules).
  useEffect(() => {
    if (customOpen) {
      const handle = window.setTimeout(() => customNameRef.current?.focus(), 0)
      return () => window.clearTimeout(handle)
    }
    return undefined
  }, [customOpen])

  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot geolocation probe + initial empty-query search; subsequent query changes go through the debounced effect.
  useEffect(() => {
    if (!navigator.geolocation) {
      void load(query, null)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        setCoords(c)
        void load(query, c)
      },
      () => void load(query, null),
      { timeout: 4000, enableHighAccuracy: false, maximumAge: 60_000 },
    )
  }, [])

  async function load(q: string, c: { lat: number; lng: number } | null) {
    setLoading(true)
    try {
      const venues = await venuesApi.search(q, c?.lat, c?.lng)
      setResults(venues)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  // Debounced live search per spec (300ms).
  // biome-ignore lint/correctness/useExhaustiveDependencies: debounced re-search on query input; coords and load are read at call-time and re-running on identity defeats the debounce.
  useEffect(() => {
    const handle = setTimeout(() => {
      void load(query, coords)
    }, 300)
    return () => clearTimeout(handle)
  }, [query])

  async function checkIn(v: VenueDto) {
    setBusy(true)
    setError(null)
    try {
      // Local venues already have a UUID. Google Places results have a
      // synthetic id — materialize them via /api/venues first so
      // POST /api/nights/:id/venues can resolve a real venue row.
      let venueId = v.id
      if (v.provider !== 'local') {
        const created = await venuesApi.create({
          name: v.name,
          city: v.city,
          address: v.address,
          latitude: v.latitude,
          longitude: v.longitude,
        })
        venueId = created.id
      }
      await nightApi.addVenue(nightId, venueId)
      navigate({ to: '/night/$id', params: { id: nightId } })
    } catch {
      setError("Couldn't tap in. Try again.")
      setBusy(false)
    }
  }

  async function commitCustom() {
    const name = customName.trim()
    if (!name) return
    setBusy(true)
    setError(null)
    try {
      // Houses: never persist coords (city-only per CLAUDE.md §7
      // private-spot rule). Bars + "other" get the user's current coords
      // as a best-guess pin location for the live map.
      const isHouse = customKind === 'house'
      const venue = await venuesApi.create({
        name,
        city: customCity.trim() || null,
        address: null,
        latitude: isHouse ? null : (coords?.lat ?? null),
        longitude: isHouse ? null : (coords?.lng ?? null),
      })

      // Tag client-side as private when house/other so the section header
      // renders + map skips it. Bars are skipped (default).
      if (customKind !== 'bar') {
        const next: Record<string, PrivateMeta> = {
          ...privateMap,
          [venue.id]: { kind: customKind, owner: customCity.trim() || undefined },
        }
        writePrivateMap(next)
        setPrivateMap(next)
      }

      await nightApi.addVenue(nightId, venue.id)
      navigate({ to: '/night/$id', params: { id: nightId } })
    } catch {
      setError("Couldn't add that place. Try again.")
      setBusy(false)
      setCustomOpen(false)
    }
  }

  // ── Partition results into public bars and private spots ─────────
  // E7: sort public bars by distance ASC (with coords) so the cap-at-5
  // shows the user's nearest options. Venues without coords sink to the
  // bottom but still appear in the unsliced source for search. Private
  // spots (houses) keep their original order — they're not pinned on the
  // map and the count is small.
  const { publicVenues, privateVenues } = useMemo(() => {
    const pub: VenueDto[] = []
    const priv: VenueDto[] = []
    for (const v of results) {
      if (privateMap[v.id]) priv.push(v)
      else pub.push(v)
    }
    if (coords) {
      const distOf = (v: VenueDto): number => {
        if (v.latitude == null || v.longitude == null) return Number.POSITIVE_INFINITY
        const dLat = v.latitude - coords.lat
        const dLng = v.longitude - coords.lng
        return dLat * dLat + dLng * dLng // squared euclidean is fine for sort
      }
      pub.sort((a, b) => distOf(a) - distOf(b))
    }
    return { publicVenues: pub, privateVenues: priv }
  }, [results, privateMap, coords])

  // E7: cap visible public list at 5 closest. Selected venue is rendered
  // separately above the slice so the user's pick is never hidden.
  const selectedPublic = useMemo(
    () =>
      selectedId
        ? (publicVenues.find((v) => v.id === selectedId && !privateMap[v.id]) ?? null)
        : null,
    [publicVenues, selectedId, privateMap],
  )
  const VENUE_VISIBLE_CAP = 5
  const visiblePublic = useMemo(() => {
    const top = publicVenues.slice(0, VENUE_VISIBLE_CAP)
    if (!selectedPublic) return top
    // If the user's selection is already in the top-5 slice, just return
    // the slice. Otherwise drop the last item to make room — selection
    // gets pinned at the top of the list via a separate render below.
    if (top.some((v) => v.id === selectedPublic.id)) return top
    return top.filter((v) => v.id !== selectedPublic.id).slice(0, VENUE_VISIBLE_CAP - 1)
  }, [publicVenues, selectedPublic])
  const pinnedSelection =
    selectedPublic && !visiblePublic.some((v) => v.id === selectedPublic.id) ? selectedPublic : null

  // ── Map projection — only public venues with coords get pinned ───
  const mappableVenues = publicVenues
    .slice(0, 12)
    .filter(
      (v): v is VenueDto & { latitude: number; longitude: number } =>
        v.latitude != null && v.longitude != null,
    )

  const userCoord: Coord | null = coords ? { latitude: coords.lat, longitude: coords.lng } : null

  const bounds = computeBounds(
    [
      ...mappableVenues.map((v) => ({ latitude: v.latitude, longitude: v.longitude })),
      ...(userCoord ? [userCoord] : []),
    ],
    0.1,
  )

  let pins: PinSpec[] = []
  let youPin: { x: number; y: number } | null = null

  if (bounds) {
    pins = mappableVenues.slice(0, 8).map((v) => ({
      ...coordToPercent({ latitude: v.latitude, longitude: v.longitude }, bounds),
      color: venueColor(v.id),
      label: v.name.charAt(0).toUpperCase(),
    }))
    if (userCoord) youPin = coordToPercent(userCoord, bounds)
  } else if (mappableVenues.length === 1 && !userCoord) {
    const only = mappableVenues[0]
    if (only) {
      pins = [
        {
          x: 50,
          y: 50,
          color: venueColor(only.id),
          label: only.name.charAt(0).toUpperCase(),
        },
      ]
    }
  } else if (userCoord) {
    youPin = { x: 50, y: 50 }
  }

  const selectedVenue = results.find((v) => v.id === selectedId) ?? null

  const showPrivateSection = privateVenues.length > 0
  const showEmpty = !loading && publicVenues.length === 0 && privateVenues.length === 0

  return (
    <div className="stumble-screen">
      <TopBar
        left={<BackButton onClick={() => navigate({ to: '/' })} />}
        eyebrow="check in"
        title="where now?"
      />
      {/* F7: fixed-grid body — map / search / results (scrollable) / tap-in.
          Height is computed against the visible viewport so the keyboard +
          iOS Safari URL bar are accounted for. Only the results row scrolls;
          the map, search input, and TAP IN button are always visible. */}
      <div
        className="screen-body"
        style={{
          display: 'grid',
          gridTemplateRows: 'auto auto 1fr auto',
          gap: 10,
          height:
            'calc(100dvh - var(--vv-bottom-chrome, 0px) - var(--safe-bottom, env(safe-area-inset-bottom)))',
          overflow: 'hidden',
        }}
      >
        <StumbleMap
          variant="treasure"
          // F7: collapse to a compact pill when the keyboard is open so the
          // search input + results stay in the visible viewport.
          height={keyboardOpen ? 64 : 160}
          compact={keyboardOpen}
          you={youPin}
          pins={pins}
          label={
            loading
              ? 'searching…'
              : pins.length === 0 && !youPin
                ? 'no coords yet'
                : `${publicVenues.length} nearby`
          }
        />

        {/* Search */}
        <div style={{ position: 'relative' }}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search bars, or type your own"
            data-testid="venue-search"
            aria-label="Search bars or type a place name"
            style={{
              minHeight: 48,
              padding: '0 38px 0 14px',
              border: '2.5px solid var(--line)',
              borderRadius: 12,
              background: 'var(--paper)',
              fontWeight: 800,
              fontSize: 16,
              boxShadow: '0 4px 0 var(--shadow-ink)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              style={{
                position: 'absolute',
                right: 6,
                top: 6,
                width: 36,
                height: 36,
                borderRadius: 999,
                border: '2px solid var(--line)',
                background: 'var(--paper-warm)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon name="close" size={14} />
            </button>
          )}
        </div>

        {/* List — capped at 5 closest. Internal scroll keeps the page itself
            non-scrolling (CLAUDE.md §5 rule 1). The 1fr grid row above gives
            this region a definite height — minHeight:0 lets it shrink so the
            internal scroll engages instead of pushing TAP IN off-screen. */}
        <div
          className="scroll-region"
          style={{
            display: 'grid',
            gap: 8,
            minHeight: 0,
            overflowY: 'auto',
          }}
        >
          {loading && results.length === 0 && (
            <div className="card body-sm muted" style={{ textAlign: 'center' }}>
              searching…
            </div>
          )}

          {showEmpty && (
            <div className="card body-sm muted" style={{ textAlign: 'center' }}>
              {query
                ? 'No matches. Try a different search or add it manually.'
                : 'No bars yet — tap "don\'t see your bar?" to add one.'}
            </div>
          )}

          {/* User's selected venue pinned at top if it would have been
              outside the top-5 slice — never hide the user's pick. */}
          {pinnedSelection && (
            <VenueRow
              key={`pinned-${pinnedSelection.id}`}
              venue={pinnedSelection}
              isHouse={false}
              selected
              onSelect={() => setSelectedId(pinnedSelection.id)}
            />
          )}

          {/* Public bars — top 5 closest (selected pinned above if outside). */}
          {visiblePublic.map((v) => (
            <VenueRow
              key={v.id}
              venue={v}
              isHouse={false}
              selected={selectedId === v.id}
              onSelect={() => setSelectedId(v.id)}
            />
          ))}

          {/* Private spots — only render if at least one exists */}
          {showPrivateSection && (
            <>
              <div className="eyebrow" style={{ paddingLeft: 4, marginTop: 4, fontSize: 10 }}>
                private spots
              </div>
              {privateVenues.map((v) => (
                <VenueRow
                  key={v.id}
                  venue={v}
                  isHouse
                  selected={selectedId === v.id}
                  onSelect={() => setSelectedId(v.id)}
                />
              ))}
            </>
          )}

          {/* "Don't see it" — always last */}
          <button
            type="button"
            onClick={() => {
              setCustomName(query)
              setCustomKind('bar')
              setCustomCity('')
              setCustomOpen(true)
            }}
            data-testid="venue-custom-cta"
            style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              gap: 12,
              padding: '12px 14px',
              border: '2.5px dashed var(--line)',
              borderRadius: 14,
              background: 'transparent',
              textAlign: 'left',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                width: 38,
                height: 38,
                borderRadius: 999,
                border: '2px dashed var(--line)',
                background: 'transparent',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Icon name="plus" size={18} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 18,
                  lineHeight: 1,
                  textTransform: 'lowercase',
                }}
              >
                don't see your bar?
              </div>
              <div className="body-sm muted" style={{ fontSize: 12 }}>
                Type it. We'll add it.
              </div>
            </div>
            <Icon name="plus" size={18} color="var(--ink)" />
          </button>
        </div>

        {error && (
          <p role="alert" style={{ color: 'var(--danger)', fontWeight: 800 }}>
            {error}
          </p>
        )}

        <button
          type="button"
          className="btn pink full"
          disabled={!selectedVenue || busy}
          data-testid="venue-tap-in"
          onClick={() => {
            if (selectedVenue) void checkIn(selectedVenue)
          }}
        >
          {busy ? 'tapping in…' : selectedVenue ? `Tap in at ${selectedVenue.name}` : "I'm here"}
        </button>
      </div>

      {customOpen && (
        <AddVenueSheet
          name={customName}
          kind={customKind}
          city={customCity}
          busy={busy}
          nameRef={customNameRef}
          onNameChange={setCustomName}
          onKindChange={setCustomKind}
          onCityChange={setCustomCity}
          onCancel={() => setCustomOpen(false)}
          onCommit={() => void commitCustom()}
        />
      )}
    </div>
  )
}

// ───── VenueRow (extracted for clarity) ─────────────────────────
function VenueRow({
  venue,
  isHouse,
  selected,
  onSelect,
}: {
  venue: VenueDto
  isHouse: boolean
  selected: boolean
  onSelect: () => void
}) {
  const color = isHouse ? 'var(--plum)' : venueColor(venue.id)
  const subtitle = isHouse
    ? 'house · city only · no map share'
    : [venue.city, venue.address].filter(Boolean).join(' · ') || 'tap to pick'

  // Stable `data-testid="venue-row"` is what chrome MCP counts. The venue id
  // moves to `data-venue-id` for tests that need to select a specific row.
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid="venue-row"
      data-venue-id={venue.id}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 12,
        padding: '10px 12px',
        border: '2.5px solid var(--line)',
        borderRadius: 14,
        background: selected ? color : isHouse ? 'var(--paper-warm)' : 'var(--paper)',
        color: selected ? 'var(--paper)' : 'var(--ink)',
        boxShadow: '0 4px 0 var(--shadow-ink)',
        textAlign: 'left',
        alignItems: 'center',
      }}
    >
      <span
        style={{
          width: 38,
          height: 38,
          borderRadius: isHouse ? 12 : 999,
          background: color,
          color: 'var(--paper)',
          border: '2px solid var(--line)',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-display)',
          fontSize: 18,
        }}
      >
        {isHouse ? '🏠' : venue.name.charAt(0).toUpperCase()}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: isHouse ? 18 : 20,
            lineHeight: 1,
            textTransform: 'lowercase',
          }}
        >
          {venue.name}
        </div>
        <div
          className="body-sm"
          style={{ opacity: 0.85, marginTop: 2, fontSize: isHouse ? 11 : 13 }}
        >
          {subtitle}
        </div>
      </div>
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          border: '2px solid currentColor',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {selected ? <Icon name="check" size={14} /> : null}
      </span>
    </button>
  )
}

// ───── AddVenueSheet (bottom sheet) ─────────────────────────────
function AddVenueSheet({
  name,
  kind,
  city,
  busy,
  nameRef,
  onNameChange,
  onKindChange,
  onCityChange,
  onCancel,
  onCommit,
}: {
  name: string
  kind: CustomKind
  city: string
  busy: boolean
  nameRef: RefObject<HTMLInputElement | null>
  onNameChange: (v: string) => void
  onKindChange: (k: CustomKind) => void
  onCityChange: (v: string) => void
  onCancel: () => void
  onCommit: () => void
}) {
  return (
    <div
      className="sheet-scrim"
      onClick={onCancel}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel()
      }}
      role="presentation"
    >
      <div
        className="sheet"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        // biome-ignore lint/a11y/useSemanticElements: see SelfieSheet.tsx — sheet needs role=dialog with aria-modal, not <dialog> (positioning needs).
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-venue-title"
      >
        <div className="grip" />
        <div className="row-between">
          <div>
            <span className="eyebrow">snap a new spot</span>
            <h2 className="h-md" id="add-venue-title">
              add it yourself.
            </h2>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>

        <input
          ref={nameRef}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="What's it called?"
          aria-label="Place name"
          data-testid="venue-custom-name"
          style={{
            minHeight: 52,
            padding: '0 14px',
            border: '2.5px solid var(--line)',
            borderRadius: 12,
            background: 'var(--paper-warm)',
            fontWeight: 800,
            fontSize: 16,
            width: '100%',
            boxSizing: 'border-box',
          }}
        />

        {/* Type pills — toggle buttons with aria-pressed.
            Why not role=radio: Biome's useSemanticElements wants <input type=radio>
            but we need the chunky chip styling; aria-pressed conveys the same
            single-selection state without a forbidden role override. */}
        <div className="row" style={{ gap: 6 }} aria-label="Place type">
          {(
            [
              ['bar', 'bar'],
              ['house', 'house'],
              ['other', 'other'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => onKindChange(k)}
              className="chip"
              data-testid={`venue-custom-kind-${k}`}
              style={{
                flex: 1,
                justifyContent: 'center',
                minHeight: 44,
                background: kind === k ? 'var(--ink)' : 'var(--paper)',
                color: kind === k ? 'var(--paper)' : 'var(--ink)',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* City — always visible (per spec: optional for vibes). Subtitle
            switches based on whether it's a house (privacy explanation). */}
        <input
          value={city}
          onChange={(e) => onCityChange(e.target.value)}
          placeholder={
            kind === 'house' ? 'city (optional — never exact location)' : 'City (just for vibes)'
          }
          aria-label="City (optional)"
          data-testid="venue-custom-city"
          style={{
            minHeight: 48,
            padding: '0 14px',
            border: '2px solid var(--line)',
            borderRadius: 12,
            background: 'var(--paper-warm)',
            fontWeight: 700,
            fontSize: 16,
            width: '100%',
            boxSizing: 'border-box',
          }}
        />

        {kind === 'house' && (
          <p
            className="body-sm muted"
            style={{ fontSize: 11, padding: '0 4px', lineHeight: 1.4, margin: 0 }}
          >
            We only store the city for houses. No address, no GPS pin on the live map. Friends see
            the venue name; strangers don't.
          </p>
        )}

        <div className="row" style={{ gap: 10 }}>
          <button type="button" className="btn secondary full" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn pink full"
            onClick={onCommit}
            disabled={!name.trim() || busy}
            data-testid="venue-custom-tap-in"
          >
            {busy ? 'adding…' : 'Add spot'}
          </button>
        </div>
      </div>
    </div>
  )
}
